import {GLTFunction, useGLTranslation} from '../hooks/useGLTranslation'
import {
  convertToAnalyticsString,
  toTitleCase,
  TranslationKey,
  TranslationOptions
} from 'notability-services-common-universal'

type FormatKey = Extract<
  TranslationKey,
  | 'counterIdentityShort'
  | 'counterIdentity'
  | 'counterThousands'
  | 'counterThousandsShort'
  | 'counterMillions'
  | 'counterMillionsShort'
>

/**
 * Round a number to the nearest one-decimal thousand, million, etc with a numeric suffix (e.g. 12.5k).
 * @param num Number
 * @returns Values (key and parameters) that can be passed directly into GLTranslation
 */
export function roundCounter(
  num: number,
  locale?: string,
  short?: boolean
): [formatKey: FormatKey, params: TranslationOptions<FormatKey>] {
  const noFraction = short && ((num >= 10000 && num < 100000) || (num >= 100000 && num < 1000000)) // When short is true, we can allow single digit fractions when there is only one integer digit e.g. 1.5k or 2.5m. but values like 15.5k or 155.5k are too long and should be 15k and 155k instead. Not handling this in the millions case just yet; that will be a great problem to have if we get there ;)

  const opts = {
    maximumFractionDigits: noFraction ? 0 : 1
  }

  if (num < 1000) {
    {
      return [
        short ? 'counterIdentityShort' : 'counterIdentity',
        {
          count: num.toLocaleString(locale, opts)
        }
      ]
    }
  } else if (num < 1000000) {
    return [
      short ? 'counterThousandsShort' : 'counterThousands',
      {
        count: (num / 1000).toLocaleString(locale, opts)
      }
    ]
  } else {
    return [
      short ? 'counterMillionsShort' : 'counterMillions',
      {
        count: (num / 1000000).toLocaleString(locale, opts)
      }
    ]
  }
}

/**
 * Convert from an enum-style string to a title case string.
 *
 * @param str String to convert, in format UPPER_CASE_WORDS
 * @returns String in title format, like "Upper Case Words"
 */
export function enumToTitle(str: string) {
  return toTitleCase(str.toLowerCase().replace('_', ' '))
}

/**
 * Convert a set of properties to have analytics-friendly keys.
 */
export function sanitizeAnalyticsPropertyBag<T>(properties: Record<string, T>) {
  return Object.entries(properties).reduce(
    (acc, pair) => {
      const [originalKey, value] = pair
      if (!Number.isNaN(value)) {
        acc[convertToAnalyticsString(originalKey)] = value
      }
      return acc
    },
    {} as Record<string, T>
  )
}

/**
 * Convert a date in the past to a 'time ago' string.
 * Example: 'today', 'yesterday', '3 days ago', '[n] weeks ago', etc.
 *
 * @param date Date to convert.
 * @param nowDate Date we are measuring "since" (default: today)
 * @returns
 */
export function useDateToElapsedString(date: Date, nowDate?: Date) {
  nowDate = nowDate ?? new Date()
  const {t, i18n} = useGLTranslation()

  return dateToElapsedString(date, t, i18n.language, {nowDate})
}

interface ElapsedDateOptions {
  nowDate?: Date
  useHourlyPrecision?: boolean
}

export function dateToElapsedString(
  date: Date,
  t: GLTFunction,
  language: string,
  {nowDate, useHourlyPrecision}: ElapsedDateOptions = {}
) {
  nowDate = nowDate ?? new Date()

  // If the date is in the future, error and default to year only.
  if (nowDate < date) {
    console.error(`useDateToElapsedString - Date is in the future: ${date}`)
    return date.toLocaleDateString(language, {year: 'numeric'})
  }

  const secondsSince = (nowDate.getTime() - date.getTime()) / 1000

  // Less than a minute ago = "seconds ago"
  if (secondsSince < 60) {
    return t('timeframeSecondsAgo')
  }

  // Less than an hour ago = "[n] minutes ago"
  const minutesSince = secondsSince / 60
  if (minutesSince < 60) {
    if (minutesSince < 2) {
      return t('timeframeOneMinuteAgo')
    }
    return t('timeframeMinutesAgo', {numMinutes: Math.floor(minutesSince)})
  }

  const MINUTES_PER_DAY = 60 * 24

  const daysSince = Math.floor(minutesSince / MINUTES_PER_DAY)
  const weeksSince = Math.round(daysSince / 7)
  const thisYear = nowDate.getFullYear() === date.getFullYear()
  const thisMonth = thisYear && nowDate.getMonth() === date.getMonth()

  if (daysSince === 0) {
    if (useHourlyPrecision) {
      const hoursSince = minutesSince / 60
      if (hoursSince < 2) {
        return t('timeframeOneHourAgo')
      }
      return t('timeframeHoursAgo', {numHours: Math.floor(hoursSince)})
    } else {
      return t('timeframeToday')
    }
  }

  if (daysSince === 1) {
    return t('timeframeYesterday')
  }

  // If it was a week (rounded) or less ago, say '[n] days ago'
  if (weeksSince <= 1) {
    return t('timeframeDaysAgo', {numDays: daysSince})
  }

  // If it was this calendar month or it was 3 weeks or less ago,
  // say '[n] weeks ago'
  if (thisMonth || weeksSince <= 3) {
    if (weeksSince > 1) {
      return t('timeframeWeeksAgo', {numWeeks: weeksSince})
    } else {
      return t('timeframeOneWeekAgo')
    }
  }

  // If it's been longer, use the '[n] months ago' if it's been less
  // than 12 months, or '[n] years ago' if longer.
  const numMonths = Math.round(daysSince / 30)
  if (numMonths < 12) {
    if (numMonths > 1) {
      return t('timeframeMonthsAgo', {numMonths})
    } else {
      return t('timeframeOneMonthAgo')
    }
  } else {
    const numYears = nowDate.getFullYear() - date.getFullYear()
    if (numYears > 1) {
      return t('timeframeYearsAgo', {numYears})
    } else {
      return t('timeframeOneYearAgo')
    }
  }
}

/**
 * Converts a date from standard js date to the
 * date that's accepted by a 'date' input field, adjusted
 * to display according to local time.
 */
export function dateToInputString(inputDate: Date) {
  const localOffsetMinutes = new Date().getTimezoneOffset() * 60000
  const date = new Date(inputDate.getTime() + localOffsetMinutes)
  const dayNumber = date.getDate()
  const day = `${dayNumber < 10 ? '0' : ''}${dayNumber}`
  const monthNumber = date.getMonth() + 1 // Because javascript.
  const month = `${monthNumber < 10 ? '0' : ''}${monthNumber}`
  return `${date.getFullYear()}-${month}-${day}`
}
